/**
 * Custom hook for managing building selection state.
 * Connects UnitSelectionController with building selection store.
 */

import { useEffect } from 'react';
import { useBuildingSelectionStore } from '@shared/stores/buildingSelectionStore';

interface UseBuildingSelectionOptions {
  scene: unknown | null;
}

/**
 * Hook for managing building selection state integration.
 */
export function useBuildingSelection({ scene }: UseBuildingSelectionOptions) {
  const setSelectedBuilding = useBuildingSelectionStore((state) => state.setSelectedBuilding);

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) {
      return;
    }

    const sceneWithMethods = scene as {
      getUnitSelectionController?: () => {
        setOnBuildingSelectionChanged?: (callback: (buildingId: string | null) => void) => void;
      } | null;
    };

    const selectionController = sceneWithMethods.getUnitSelectionController?.();

    if (selectionController?.setOnBuildingSelectionChanged) {
      // Set up callback to update store when building selection changes
      selectionController.setOnBuildingSelectionChanged((buildingId: string | null) => {
        setSelectedBuilding(buildingId);
      });
    }

    // Cleanup: remove callback when component unmounts or scene changes
    return () => {
      if (selectionController?.setOnBuildingSelectionChanged) {
        selectionController.setOnBuildingSelectionChanged(() => {
          // Empty handler for cleanup
        });
      }
    };
  }, [scene, setSelectedBuilding]);
}

